<template>
  <div class="page page-with-bg page-cancelable">
    <div class="page-form-wrap">
      <div class="page-form-cancel">
        <b-btn class="page-cancel-button"
               variant="link"
               type="submit"
               to="/reports">
          <svg-icon name="close" /> Cancel
        </b-btn>
      </div>
      <div class="page-form-header small-header">
        <h1>Order an Individual Risk Assessment</h1>
      </div>
      <div class="page-form-content">
        <b-form @submit.prevent="submit">
          <div class="mb-5">
            <label>Search for Existing Patient w/ Previous Order</label>
            <v-form-group label="First or Last Name">
              <dt-filter-input-autocomplete placeholder="First or Last Name"
                                            @selected="onSelectPatient"
                                            :items="patientSearchOptions"
                                            slot-scope="{ attrs, listeners }"
                                            :inputAttrs="attrs"
                                            :inputListeners="listeners" />
            </v-form-group>
          </div>
          <label>Add New Patient</label>
          <div class="row">
            <v-form-group class="col-12 col-md-6"
                          :validator="$v.patientFirstName"
                          label="First Name">
              <b-form-input v-model="patientFirstName"
                            placeholder="First Name"
                            slot-scope="{ attrs, listeners }"
                            v-bind="attrs"
                            v-on="listeners" />
            </v-form-group>
            <v-form-group class="col-12 col-md-6"
                          :validator="$v.patientLastName"
                          label="Last Name">
              <b-form-input slot-scope="{ attrs, listeners }"
                            v-bind="attrs"
                            v-on="listeners"
                            v-model="patientLastName"
                            placeholder="Last Name" />
            </v-form-group>
          </div>
          <v-form-group :validator="$v.patientEmail"
                        label="Email">
            <b-form-input slot-scope="{ attrs, delayedListeners }"
                          v-bind="attrs"
                          v-on="delayedListeners"
                          v-model="patientEmail"
                          placeholder="Email" />
          </v-form-group>
          <v-form-group label="(Optional) Patient ID / MRN">
            <b-form-input slot-scope="{ attrs, delayedListeners }"
                          v-bind="attrs"
                          v-on="delayedListeners"
                          v-model="patientMRN"
                          placeholder="Patient ID / MRN" />
          </v-form-group>
          <v-form-group v-if="isEmployerTaggingAccessible"
                        label="(Optional) Employer">
            <v-select ref="employerDropdown"
                      slot-scope="{ attrs, listeners }"
                      v-model="selectedEmployer"
                      v-bind="attrs"
                      v-on="listeners"
                      :options="tagOptions"
                      placeholder="(Optional) Employer"
                      :clearable="false"
                      :class="[{ 'vs--has-value': selectedEmployer !== null }]"
                      id="clientType"
                      @input="onSelectEmployer" />
          </v-form-group>
          <b-form-group class="form-buttons">
            <b-btn variant="success"
                   type="submit">
              Order Analysis
            </b-btn>
          </b-form-group>

          <validation-summary :errors="errors"></validation-summary>
          <template v-if="!isEmailViable">
            <p>
              If this is for patient from your your practice, please Search by name <br />
              in the Search field then select "Order Analysis".
            </p>
            <p>
              If the patient does not show up in Search, it is possible they have an <br />
              account with another client. <br />
              Please contact <a href="mailto: support@precisionhealthreports.com">support@precisionhealthreports.com</a> and we can <br />
              realign this patient to you.
            </p>
          </template>
        </b-form>
      </div>
    </div>
    <tag-create-edit-modal v-if="client"
                           ref="tagCreateModal"
                           @success="onTagCreateSuccess"
                           @cancel="onTagCreateCancel"
                           :clientId="client.id"
                           :type="TagType.Employer" />
    <b-modal ref="activeReport"
             cancel-variant="outline-secondary"
             ok-variant="success"
             @ok="createOrder"
             title="Confirm Order">
      <template #default>
        <div>
          This patient currently has an open order. Are you
          sure you would like to place a new order?
          <br />
          <br />
          If the patient has another order that you would like to
          cancel, you can click on the Order’s status to cancel
          it.
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import { requiredIf, email } from "vuelidate/lib/validators";
  import formMixin from "mixins/form-mixin";

  import { createNamespacedHelpers, mapState, mapActions } from 'vuex';
  const { mapGetters } = createNamespacedHelpers('report');

  import patientService from "services/patient-service";
  import clientService from "services/client-service";
  import userService from "services/user-service";
  import featureService from "services/feature-service";
  import reportService from "services/report-service";

  import tagCreateEditModal from "@/components/common/modals/tag-create-edit-modal.vue";

  import TagType from "enums/tag-type";

  import {
    emailViabilityError,
    employerTagging,
    emptyOptionValue
  } from "@/constants";
  export default {
    components: {
      tagCreateEditModal
    },
    mixins: [formMixin],
    data() {
      return {
        patientFirstName: "",
        patientLastName: "",
        patientEmail: "",
        patientMRN: "",
        client: null,

        selectedPatient: null,
        selectedEmployer: null,

        patientSearchOptions: [],
        tagOptions: [],

        adminFeatures: [],

        isEmailViable: true,
        hasReportInProgress: false,

        TagType
      };
    },

    computed: {
      ...mapState('report', ["report"]),
      ...mapState('tag', ["tag", "tags"]),
      ...mapGetters(["hasSelectedPatient"]),

      isAdmin() {
        return this.$auth.isInRole("Admin");
      },

      isClientAdmin() {
        return this.$auth.isInRole("ClientAdmin");
      },

      isEmployerTaggingAccessible() {
        if (this.adminFeatures.length) {
          return this.adminFeatures.some(f => f.identifier === employerTagging)
        }

        return this.$auth.isFeatureAvailable(employerTagging);
      }
    },

    validations: {
      patientFirstName: {
        required: requiredIf(function () { return !this.hasSelectedPatient; })
      },
      patientLastName: {
        required: requiredIf(function () { return !this.hasSelectedPatient; })
      },
      patientEmail: {
        required: requiredIf(function () { return !this.hasSelectedPatient; }),
        email
      }
    },

    methods: {
      ...mapActions('report', ['selectPatient', 'clear']),
      ...mapActions('tag', ['getTagSelectList']),

      async onSelectPatient(patient) {
        this.selectedPatient = patient;
        this.selectPatient(patient);

        if (!patient) {
          this.hasReportInProgress = null;

          return;
        }

        if (this.isAdmin || this.isClientAdmin) {
          this.hasReportInProgress = await reportService.isReportInProgress(this.$route.query.id, patient.value);
        }
        else {
          let client = await clientService.getByUserId(parseInt(this.$auth.userInfo.sub));

          this.hasReportInProgress = await reportService.isReportInProgress(client.id, patient.value);
        }
      },

      async onSubmit() {
        if (this.hasReportInProgress) {
          this.$refs.activeReport.show();
          return;
        }

        this.createOrder();
      },

      async createOrder() {
        this.report.patientFirstName = this.patientFirstName;
        this.report.patientLastName = this.patientLastName;
        this.report.patientEmail = this.patientEmail;
        this.report.patientMRN = this.patientMRN;
        this.report.employerTagId = this.selectedEmployer ? parseInt(this.selectedEmployer.value) : null;

        try {
          if (!this.selectedPatient)
            await userService.verifyEmail(this.report.patientEmail);
        }
        catch (error) {
          this.errors = error.customErrors;

          this.isEmailViable = !this.errors.some(e => e === emailViabilityError);

          return;
        }

        if (this.isAdmin || this.isClientAdmin) {
          this.$router.push({ name: "create/type", params: { clientId: this.$route.query.id } });
        }
        else {
          let client = await clientService.getByUserId(parseInt(this.$auth.userInfo.sub));

          this.$router.push({ name: "create/type", params: { clientId: client.id } });
        }
      },

      onSelectEmployer(option) {
        if (option.value === emptyOptionValue) {
          this.$refs.tagCreateModal.show();
        }
      },

      onTagCreateCancel() {
        this.selectedEmployer = null;
      },

      async onTagCreateSuccess() {
        await this.setTagOptions(this.tag.id);

        this.$root.successToast("Employer tag successfully created");
      },

      async setTagOptions(tagId) {
        await this.getTagSelectList({ clientId: this.client.id, isForFilter: false });
        this.tagOptions = this.tags;

        if (tagId) {
          this.selectedEmployer = this.tagOptions.find(o => parseInt(o.value) === tagId);
        }
      }
    },

    async mounted() {
      this.clear();

      if (this.isAdmin && this.$route.query.id) {
        let features = await featureService.getClientFeatures(this.$route.query.id);

         this.adminFeatures = features;
      }

      if (this.isEmployerTaggingAccessible) {
        if (this.isAdmin && this.$route.query.id) {
          this.client = await clientService.get(this.$route.query.id);
        }
        else {
          this.client = await clientService.getByUserId(parseInt(this.$auth.userInfo.sub));
        }
        await this.setTagOptions();
      }

      this.patientSearchOptions = await patientService.getAsSelectListItems(this.$route.query.id && this.isAdmin || this.isClientAdmin ? this.$route.query.id : null, true);
    }
  };
</script>
<style lang="scss" scoped>
  @import '@/styles/mixins';

  .page {
    @include retina('/png/doctor.png');
  }
</style>